import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

// The sidebar's Data sources icon, as a component (GenericIcons holds only its URL).
import LinkIcon from '@/assets/icons/link_line.svg?component';
import TitleIcon from '@/components/atoms/TitleIcon/TitleIcon';
import type { ProviderGroup } from '@/domain/providerConfig';
import { formatDateTimeWorded } from '@/utils/date';
import { parseLocale } from '@/utils/localeFormatters';

import { cardHeaderSummary } from './cardHeaderSummary';
import { StatusPill } from './StatusPill';

import styles from './ProviderCard.module.scss';

/** What a provider card hands ContentBlock for its header. */
export interface CardHeaderParts {
    titleIcon: ReactNode;
    /** The last fetch, or undefined for a card without rows. */
    subtitle: string | undefined;
    headerActions: ReactNode;
}

/** The ContentBlock header parts of a provider card: its icon, last fetch and status. */
export function useCardHeader(group: ProviderGroup | null): CardHeaderParts {
    const { t, i18n } = useTranslation();
    const summary = cardHeaderSummary(group?.rows ?? null);
    const fetched = summary.lastFetchedAt;
    const subtitle = !summary.hasRows
        ? undefined
        : fetched === null
          ? t('providerConfig.pens.neverFetched')
          : t('providerConfig.pens.lastFetched', {
                time: formatDateTimeWorded(fetched, 'second', parseLocale(i18n.language)),
            });
    return {
        // SvgIcon sets no aria attribute, so the wrapper keeps the icon out of the tree.
        titleIcon: (
            <span aria-hidden="true">
                <TitleIcon icon={LinkIcon} />
            </span>
        ),
        subtitle,
        headerActions: (
            <span className={styles.headerStatus}>
                <StatusPill tone={summary.tone} label={t(summary.labelKey)} />
                {summary.hasRows && (
                    <span className={styles.pensActive}>
                        {t('providerConfig.card.pensActive', {
                            count: summary.pens,
                            active: summary.activePens,
                        })}
                    </span>
                )}
            </span>
        ),
    };
}
